import type { ComponentPropsWithoutRef } from "react";
import ReactMarkdown, {
  type Components,
  type ExtraProps,
} from "react-markdown";
import remarkGfm from "remark-gfm";
import { Link, useParams } from "react-router-dom";
import ProjectSection from "../components/ProjectSection";
import { findProject } from "../content/projects";
import { projectPurposeDetails } from "../portfolio/projectPresentation";
import type { PublicOverviewProject, ReadmeProject } from "../portfolio/types";
import "../styles/readme.css";
import NotFoundPage from "./NotFoundPage";

const README_COMPONENTS: Components = {
  a: SafeReadmeLink,
  img: OmittedReadmeImage,
};

/** Renders the generated publication projection selected by the project route. */
export default function ProjectPage() {
  const { projectId = "" } = useParams();
  const project = findProject(projectId);

  if (!project) {
    return <NotFoundPage />;
  }

  return project.sourceType === "public-overview" ? (
    <PublicOverviewPage project={project} />
  ) : (
    <ReadmePage project={project} />
  );
}

/** Renders a structured stable-release narrative with the primary purpose in the hero and only additional purpose detail below. */
function PublicOverviewPage({ project }: { project: PublicOverviewProject }) {
  const purposeDetails = projectPurposeDetails(project.purpose);

  return (
    <article className="project-detail">
      <ProjectBackLink />
      <header className="project-hero project-hero-editorial">
        <div className="project-kicker">
          <span>v{project.version}</span>
          <span>{project.sourceVisibility} source</span>
          <span>released {project.releaseDate}</span>
        </div>
        <h1>{project.title}</h1>
        <p>{project.purpose[0]}</p>
        <ProjectSourceMeta
          repositoryUrl={project.repositoryUrl}
          privateMessage="Source repository is private; this release overview is intentionally public."
        />
      </header>

      <div className="project-story">
        {purposeDetails.length > 0 && (
          <ProjectSection title="Project purpose">
            {purposeDetails.map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}
          </ProjectSection>
        )}

        <ProjectSection title="What it does">
          <EvidenceList values={project.whatItDoes} />
        </ProjectSection>

        <ProjectSection title="How it works">
          {project.howItWorks.map((paragraph) => (
            <p key={paragraph}>{paragraph}</p>
          ))}
        </ProjectSection>

        <ProjectSection title="Release highlights">
          <EvidenceList values={project.releaseHighlights} />
        </ProjectSection>

        <ProjectSection title="Current limitations">
          <EvidenceList values={project.currentLimitations} />
        </ProjectSection>

        <ProjectSection title="Validation">
          <EvidenceList values={project.validation} />
        </ProjectSection>
      </div>
    </article>
  );
}

/** Renders a sanitized README publication exception with optional curated engineering context before its archived source snapshot. */
function ReadmePage({ project }: { project: ReadmeProject }) {
  return (
    <article className="project-detail">
      <ProjectBackLink />
      <header className="project-hero project-hero-editorial">
        <div className="project-kicker">
          <span>{project.label}</span>
          <span>{project.sourceVisibility} source</span>
          <span>README snapshot</span>
        </div>
        <h1>{project.title}</h1>
        <p>{project.summary}</p>
        <ProjectSourceMeta
          repositoryUrl={project.repositoryUrl}
          privateMessage="Source repository is private; this reviewed README snapshot is intentionally public."
        />
      </header>

      <div className="project-story readme-project-story">
        {project.portfolioHighlights ? (
          <>
            <ProjectSection title="Engineering context">
              <EvidenceList values={project.portfolioHighlights} />
            </ProjectSection>

            <ProjectSection title="Archived README">
              <div className="readme-markdown">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  skipHtml
                  components={README_COMPONENTS}
                >
                  {project.markdown}
                </ReactMarkdown>
              </div>
            </ProjectSection>
          </>
        ) : (
          <div className="readme-markdown">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              skipHtml
              components={README_COMPONENTS}
            >
              {project.markdown}
            </ReactMarkdown>
          </div>
        )}
      </div>
    </article>
  );
}

/** Renders the consistent route back to the project index. */
function ProjectBackLink() {
  return (
    <Link className="back-link" to="/#projects">
      ← Back to projects
    </Link>
  );
}

/** Renders either a safe public repository link or the publication-boundary message for a private source. */
function ProjectSourceMeta({
  repositoryUrl,
  privateMessage,
}: {
  repositoryUrl: string | null;
  privateMessage: string;
}) {
  return (
    <div className="project-meta">
      {repositoryUrl ? (
        <a href={repositoryUrl} target="_blank" rel="noreferrer">
          Source code ↗
        </a>
      ) : (
        <span>{privateMessage}</span>
      )}
    </div>
  );
}

/** Renders one imported overview section as evidence-oriented statements without interpreting Markdown as HTML. */
function EvidenceList({ values }: { values: string[] }) {
  return (
    <ul className="evidence-list">
      {values.map((value) => (
        <li key={value}>{value}</li>
      ))}
    </ul>
  );
}

/** Renders only same-document fragments or explicit external links from README Markdown. */
function SafeReadmeLink({
  href,
  children,
  node: _node,
  ...props
}: ComponentPropsWithoutRef<"a"> & ExtraProps) {
  if (!href || !isSafeReadmeHref(href)) {
    return <span>{children}</span>;
  }

  const external = !href.startsWith("#");
  return (
    <a
      {...props}
      href={href}
      target={external ? "_blank" : undefined}
      rel={external ? "noreferrer" : undefined}
    >
      {children}
    </a>
  );
}

/** Suppresses README images so private repository-relative assets never become runtime fetches or disclosure paths. */
function OmittedReadmeImage(): null {
  return null;
}

/** Accepts only fragment, HTTP(S), or mail destinations after README sanitization. */
function isSafeReadmeHref(href: string): boolean {
  return (
    href.startsWith("#") || /^https?:\/\//i.test(href) || /^mailto:/i.test(href)
  );
}
